import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import useJellyIndicator from "./useJellyIndicator";
import "./SidebarJelly.css";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, User, MessageSquare, CalendarCheck,
  Trophy, Video, Sword, Award, LogOut, TrendingUp, FileText, Settings, ChevronLeft, ChevronRight,
} from "lucide-react";

const athleteNav = [
  { to: "/atleta", end: true, label: "Painel", icon: LayoutDashboard, testid: "athlete-nav-dashboard" },
  { to: "/atleta/perfil", label: "Perfil", icon: User, testid: "athlete-nav-profile" },
  { to: "/atleta/plano", label: "Plano de Treinamento", icon: FileText, testid: "athlete-nav-plan" },
  { to: "/atleta/feedback", label: "Feedback", icon: MessageSquare, testid: "athlete-nav-feedback" },
  { to: "/atleta/frequencia", label: "Frequência", icon: CalendarCheck, testid: "athlete-nav-attendance" },
  { to: "/atleta/partidas", label: "Partidas", icon: Sword, testid: "athlete-nav-games" },
  { to: "/atleta/videos", label: "Materiais", icon: Video, testid: "athlete-nav-videos" },
  { to: "/atleta/evolucao", label: "Evolução", icon: TrendingUp, testid: "athlete-nav-evolution" },
  { to: "/atleta/ranking", label: "Ranking GSX", icon: Award, testid: "athlete-nav-ranking" },
  { to: "/atleta/torneios", label: "Torneios", icon: Trophy, testid: "athlete-nav-tournaments" },
];

const adminNav = [
  { to: "/admin", end: true, label: "Visão Geral", icon: LayoutDashboard, testid: "admin-nav-overview" },
  { to: "/admin/atletas", label: "Atletas", icon: User, testid: "admin-nav-athletes" },
  { to: "/admin/feedbacks", label: "Feedbacks", icon: MessageSquare, testid: "admin-nav-feedbacks" },
  { to: "/admin/hall-da-fama", label: "Hall da Fama", icon: Award, testid: "admin-nav-hall-of-fame" },
  { to: "/admin/frequencia", label: "Frequência / Chamada", icon: CalendarCheck, testid: "admin-nav-attendance" },
  { to: "/admin/competicoes", label: "Desempenho em Competições", icon: Award, testid: "admin-nav-competitions" },
  { to: "/admin/torneios", label: "Torneios", icon: Trophy, testid: "admin-nav-tournaments" },
  { to: "/admin/aulas-experimentais", label: "Aulas Exp.", icon: MessageSquare, testid: "admin-nav-experimental" },
  { to: "/admin/videos", label: "Materiais", icon: Video, testid: "admin-nav-videos" },
  { to: "/admin/ranking", label: "Ranking Interno GSX", icon: Trophy, testid: "admin-nav-ranking" },
  { to: "/admin/ranking-config", label: "Config. Ranking", icon: Settings, testid: "admin-nav-ranking-config" },
];

const SIDEBAR_STORAGE_KEY = "gsx-sidebar-collapsed";
// Set to false to restore the previous athlete and admin card palette.
const ENABLE_APP_CARD_THEME = true;

export default function AppLayout({ children, kind = "athlete" }) {
  const nav = kind === "admin" ? adminNav : athleteNav;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activePath = nav.find((item) => location.pathname === item.to || (!item.end && location.pathname.startsWith(`${item.to}/`)))?.to || null;
  const { trackRef, itemRefs, phase, variables, indicatorStyle } = useJellyIndicator(activePath, "y", `sidebar-${kind}`);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleLogout = async () => { navigate("/", { replace: true }); await logout(); };
  const toggleSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next)); } catch { /* Storage unavailable. */ }
  };

  return (
    <div className={`gsx-app-shell flex min-h-screen bg-[#061426] text-slate-100${ENABLE_APP_CARD_THEME ? " gsx-app-card-theme" : ""}`}>
      <aside className={`gsx-sidebar sticky top-0 hidden h-screen shrink-0 flex-col overflow-hidden border-r border-amber-500/10 bg-[#071A33] transition-[width,padding] duration-300 lg:flex ${collapsed ? "w-[76px] p-3" : "w-64 p-5"}`}>
        <div className="mb-6 flex flex-col items-center gap-3">
          <Link to="/" aria-label="Voltar ao início" className="flex justify-center">
            <Logo size={collapsed ? 40 : 64} />
          </Link>
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={collapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
            aria-expanded={!collapsed}
            aria-controls="gsx-sidebar-navigation"
            title={collapsed ? "Expandir menu" : "Recolher menu"}
            data-testid="sidebar-toggle"
            className={`flex h-8 w-8 items-center justify-center rounded-lg border border-amber-400/25 bg-amber-400/5 text-amber-300 transition-colors hover:border-amber-400/50 hover:bg-amber-400/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400 ${collapsed ? "self-center" : "self-end"}`}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        <nav ref={trackRef} id="gsx-sidebar-navigation" aria-label={kind === "admin" ? "Menu administrativo" : "Menu do atleta"} className="sidebar-jelly relative flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden" style={variables}>
          <span className="sidebar-jelly__indicator" data-phase={phase} style={indicatorStyle} aria-hidden="true" />
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              ref={(element) => { itemRefs.current[n.to] = element; }}
              title={collapsed ? n.label : undefined}
              data-testid={n.testid}
              className={({ isActive }) =>
                `relative z-10 flex min-h-10 items-center rounded-lg py-2.5 text-sm transition-colors ${collapsed ? "justify-center px-1" : "gap-3 px-3"} ${
                  isActive ? "text-amber-300" : "text-slate-300 hover:bg-white/5"}`}
            >
              <n.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className={collapsed ? "sr-only" : ""}>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="shrink-0 border-t border-white/5 pt-4">
          {!collapsed && <div className="mb-2 truncate text-xs text-slate-400">{user?.name}</div>}
          <Button
            variant="outline"
            onClick={handleLogout}
            title={collapsed ? "Sair" : undefined}
            aria-label="Sair"
            data-testid="btn-logout"
            className="w-full border-amber-500/30 bg-transparent px-2 text-slate-100 hover:bg-amber-500/10"
          >
            <LogOut className={`h-4 w-4 shrink-0 ${collapsed ? "" : "mr-2"}`} aria-hidden="true" />
            <span className={collapsed ? "sr-only" : ""}>Sair</span>
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="gsx-mobile-header sticky top-0 z-30 flex items-center justify-between border-b border-amber-500/10 bg-[#061426]/95 px-4 py-3 backdrop-blur lg:hidden">
          <Logo size={40} />
          <Button size="sm" variant="outline" onClick={handleLogout} aria-label="Sair"
            className="border-amber-500/30 bg-transparent text-slate-100">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <div className="gsx-mobile-nav overflow-x-auto border-b border-amber-500/10 bg-[#071A33] lg:hidden">
          <div className="flex min-w-max gap-1 px-2 py-2">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-1.5 text-xs ${
                    isActive ? "bg-amber-500/20 text-amber-300"
                             : "text-slate-300"}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="gsx-main flex-1 p-4 sm:p-6 lg:p-10">
          {children}
        </main>
      </div>
    </div>
  );
}
